import { RootHorizontalLogo } from '@/assets/logos/root-horizontal';
import { RouteFn, SubmitFn } from '@/hooks/use-flow';
import { Component } from '@/schemas/sceneDataSchema';
import { styled } from '@mui/material';
import { DynamicRenderer } from '@/components/DynamicUI/DynamicRenderer';
import { ExternalImage } from '@/components/DynamicUI/ExternalImage';
import { useState } from 'react';
import { useScrollPosition } from '@/hooks/use-scroll-position';

interface HeaderProps {
  components: Component[];
  route: RouteFn;
  submit: SubmitFn;
  logoSrc?: string | null;
  showHeader?: boolean;
}

export const AppHeader = ({ components, route, submit, logoSrc, showHeader = true }: HeaderProps) => {
  const [isNavHidden, setIsNavHidden] = useState(false);

  useScrollPosition(({ previousPosition, currentPosition }) => {
    const isAtPageTop = currentPosition.y > -50;

    const shouldHideNav = currentPosition.y < previousPosition.y && !isAtPageTop;
    setIsNavHidden(shouldHideNav);
  });

  if (!showHeader) {
    return null;
  }

  return (
    <Header isNavHidden={isNavHidden}>
      <LogoContainer>
        {logoSrc ? (
          <ExternalImage
            src={logoSrc}
            width={null}
            margin={'0'}
            alt={'logo'}
          />
        ) : (
          <RootHorizontalLogo />
        )}
      </LogoContainer>
      <HeaderComponentsContainer>
        <DynamicRenderer
          components={components}
          route={route}
          submit={submit}
        />
      </HeaderComponentsContainer>
    </Header>
  );
};

const Header = styled('header')<{ isNavHidden: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  height: var(--app-header-height);
  padding: 10px 24px;
  z-index: 10;
  background-color: var(--mui-palette-background-main);
  border-bottom: 1px solid var(--mui-palette-border-secondary);
  /* ↓ Hide header on scroll ↓ */
  position: fixed;
  top: 0;
  transition: transform 500ms cubic-bezier(0.37, 0, 0.28, 1);
  transform: ${({ isNavHidden }) => (isNavHidden ? 'translate3d(0, -100%, 0)' : 'translate3d(0, 0, 0)')};

  & button {
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
  }
`;

const LogoContainer = styled('div')`
  display: flex;
  align-items: center;
  height: 100%;
  width: min(50%, 200px);

  & img {
    max-height: 100%;
    object-fit: contain;
    object-position: left center;
  }
`;

const HeaderComponentsContainer = styled('div')`
  display: flex;
  align-items: center;
`;
